import { useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Phone, MapPin, ChevronRight, Crosshair, Monitor, Music, Gem, Wrench, Target, ArrowLeft } from 'lucide-react';
import CITIES, { getCityUrl, parseCityFromUrl } from '@/data/cities';
import { REDIRECTS } from '@/components/OldPageRedirect';

const LOGO_URL = "/images/cave-city-pawn-logo.png";
const CAVEMAN_CARRY = "/images/caveman-carrying.png";

const categories = [
  { name: 'Firearms & Guns', icon: Crosshair, slug: 'firearms',
    text: 'handguns, rifles, shotguns, and ammunition' },
  { name: 'Electronics', icon: Monitor, slug: 'electronics',
    text: 'laptops, tablets, smartphones, gaming consoles, and TVs' },
  { name: 'Musical Instruments', icon: Music, slug: 'instruments',
    text: 'guitars, amps, drums, keyboards, and more' },
  { name: 'Jewelry & Gold', icon: Gem, slug: 'jewelry',
    text: 'gold, silver, diamonds, watches, and fine jewelry' },
  { name: 'Power Tools', icon: Wrench, slug: 'tools',
    text: 'DeWalt, Milwaukee, Makita drills, saws, and more' },
  { name: 'Archery Equipment', icon: Target, slug: 'archery',
    text: 'compound bows, recurve bows, crossbows, arrows, and accessories' },
];

function getCityData(slug) {
  return parseCityFromUrl(slug);
}

function SEOHead({ city }) {
  const fullName = `${city.name}, ${city.state}`;
  const title = `Pawn Shop Near ${fullName} | Cave City Pawn Shop & Trading Company`;
  const desc = `Looking for a pawn shop near ${fullName}? Cave City Pawn Shop is just ${city.distance} miles ${city.direction} in Marianna, FL. Buy, sell, or pawn firearms, electronics, jewelry, instruments, tools & archery equipment. No credit check loans. Call 850-615-6105.`;

  useEffect(() => {
    document.title = title;
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', desc);
    setMeta('keywords', `pawn shop ${city.name}, pawn shop near ${fullName}, buy sell pawn ${city.name}, firearms ${city.name}, jewelry ${city.name}, electronics ${city.name}, pawn loans ${city.name}, archery ${city.name}, Cave City Pawn`);
    setMeta('og:title', title, 'property');
    setMeta('og:description', desc, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:image', LOGO_URL, 'property');

    // Schema.org
    let schema = document.getElementById('city-schema');
    if (!schema) { schema = document.createElement('script'); schema.id = 'city-schema'; schema.type = 'application/ld+json'; document.head.appendChild(schema); }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Cave City Pawn Shop & Trading Company",
      "image": LOGO_URL,
      "telephone": "850-615-6105",
      "address": { "@type": "PostalAddress", "streetAddress": "4417 Jackson Street", "addressLocality": "Marianna", "addressRegion": "FL", "postalCode": "32448", "addressCountry": "US" },
      "areaServed": { "@type": "City", "name": city.name, "containedInPlace": { "@type": "State", "name": city.state === 'FL' ? 'Florida' : city.state === 'AL' ? 'Alabama' : 'Georgia' } },
      "description": desc,
      "openingHoursSpecification": [
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "10:00", "closes": "17:00" },
        { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "14:00" }
      ]
    });

    return () => {
      document.title = 'Cave City Pawn Shop | Buy, Sell & Pawn Guns, Electronics, Jewelry & More';
      const s = document.getElementById('city-schema');
      if (s) s.remove();
    };
  }, [city, title, desc, fullName]);

  return null;
}

export default function CityLandingPage() {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const city = getCityData(citySlug);

  useEffect(() => { window.scrollTo(0, 0); }, [citySlug]);

  // Handle redirects for old Wix pages that hit this catch-all route
  useEffect(() => {
    const path = location.pathname.toLowerCase().replace(/\/$/, '') || '/';
    const target = REDIRECTS[path];
    if (target && !city) {
      if (target.startsWith('http')) {
        window.location.href = target;
      } else if (target.includes('#')) {
        const [base, hash] = target.split('#');
        navigate(base || '/', { replace: true });
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      } else {
        navigate(target, { replace: true });
      }
    }
  }, [location.pathname, navigate, city]);

  if (!city) {
    // Check if it's a known redirect (will be handled by useEffect above)
    const path = location.pathname.toLowerCase().replace(/\/$/, '');
    if (REDIRECTS[path]) return null;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-8">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-dirt-brown uppercase mb-4">Page Not Found</h1>
          <Link to="/" className="cave-btn inline-flex items-center gap-2 px-6 py-3 bg-magma-red text-white font-heading uppercase rounded-rock shadow-deep">
            <ArrowLeft size={18} /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const fullName = `${city.name}, ${city.state}`;
  const stateFull = city.state === 'FL' ? 'Florida' : city.state === 'AL' ? 'Alabama' : 'Georgia';
  const nearbyCities = CITIES.filter(c => c.slug !== city.slug && Math.abs(c.distance - city.distance) < 20).slice(0, 8);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SEOHead city={city} />

      {/* Nav Bar */}
      <nav className="bg-dirt-brown/95 backdrop-blur-sm shadow-deep py-2 sticky top-0 z-50" data-testid="city-navbar">
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-900 via-amber-700 to-amber-900 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="city-nav-logo">
            <img src={LOGO_URL} alt="Cave City Pawn Shop" className="h-10" width="auto" height={40} />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer" data-testid="city-shop-online-btn"
              className="cave-btn px-4 py-2 bg-magma-red text-white font-heading text-xs sm:text-sm uppercase tracking-wider rounded-rock shadow-deep flex items-center gap-1.5">
              <ShoppingCart size={14} /> Shop Online
            </a>
            <a href="tel:850-615-6105" data-testid="city-call-btn"
              className="cave-btn px-3 py-2 border-2 border-accent text-accent font-heading text-xs sm:text-sm uppercase rounded-rock flex items-center gap-1.5 hover:bg-accent hover:text-dirt-brown transition-colors">
              <Phone size={14} /> <span className="hidden sm:inline">850-615-6105</span><span className="sm:hidden">Call</span>
            </a>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative py-16 md:py-24 cave-gradient noise-overlay overflow-hidden" data-testid="city-hero">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-sm font-body text-stone-light/70">
                <li><Link to="/" className="hover:text-accent transition-colors">Home</Link></li>
                <li><ChevronRight size={14} /></li>
                <li><Link to="/locations" className="hover:text-accent transition-colors">Locations</Link></li>
                <li><ChevronRight size={14} /></li>
                <li className="text-accent">{fullName}</li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading text-bone-white uppercase tracking-wide mb-5 leading-tight" data-testid="city-h1">
                  Pawn Shop Near<br />
                  <span className="text-accent">{fullName}</span>
                </h1>
                <p className="text-base md:text-lg text-stone-light font-body leading-relaxed mb-6">
                  {city.distance === 0
                    ? `Welcome to Cave City Pawn Shop & Trading Company, located right here in ${city.name}, ${stateFull}! We are your local destination for buying, selling, and pawning quality items.`
                    : `Located just ${city.distance} miles ${city.direction} of ${city.name} in Marianna, Florida, Cave City Pawn Shop & Trading Company is the trusted pawn shop serving ${city.name}, ${stateFull} and the surrounding area.`
                  }
                  {' '}With over 35 years of experience, we offer the best deals on firearms, electronics, musical instruments, jewelry, power tools, and archery equipment.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <a href="tel:850-615-6105" data-testid="city-hero-call"
                    className="cave-btn inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-magma-red text-white font-heading text-base uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                    <Phone size={20} /> Call 850-615-6105
                  </a>
                  <a href="https://www.google.com/maps/dir/?api=1&destination=4417+Jackson+Street,+Marianna,+FL+32448" target="_blank" rel="noopener noreferrer" data-testid="city-hero-directions"
                    className="cave-btn inline-flex items-center justify-center gap-2 px-7 py-3.5 border-3 border-bone-white text-bone-white font-heading text-base uppercase rounded-rock hover:bg-bone-white hover:text-dirt-brown transition-all">
                    <MapPin size={20} /> Get Directions
                  </a>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['No Credit Checks', 'Same Day Cash', '35+ Years Exp.', `${city.distance === 0 ? 'Right Here!' : city.distance + ' Miles Away'}`].map(tag => (
                    <span key={tag} className="px-3 py-1 bg-dirt-brown/50 text-stone-light text-xs font-body font-semibold rounded-full border border-stone-dark/30 backdrop-blur-sm">{tag}</span>
                  ))}
                </div>
              </div>
              <div className="hidden lg:flex items-center justify-center">
                <img src={CAVEMAN_CARRY} alt="Cave City Pawn mascot" className="w-52 caveman-float drop-shadow-xl" loading="lazy" />
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 z-10">
            <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
              <path d="M0 80L60 68C120 56 240 32 360 26C480 20 600 32 720 38C840 44 960 44 1080 38C1200 32 1320 20 1380 14L1440 8V80H0Z" fill="#F4EBD9"/>
            </svg>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 md:py-24 bg-background noise-overlay" data-testid="city-categories">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-sm font-body font-semibold text-magma-red uppercase tracking-widest mb-3">What We Carry</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading text-dirt-brown uppercase chisel-heading">
                Buy, Sell & Pawn in <span className="text-magma-red">{city.name}</span>
              </h2>
              <p className="text-base text-muted-foreground font-body max-w-2xl mx-auto mt-6">
                Residents of {fullName} trust Cave City Pawn for fair deals and honest appraisals. Here is what you will find at our shop:
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <div key={cat.slug} data-testid={`city-cat-${cat.slug}`}
                    className="stone-card bg-card border-3 border-dirt-brown/15 rounded-rock-lg p-6 group">
                    <div className="category-icon w-12 h-12 bg-magma-red/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-magma-red transition-colors duration-300">
                      <Icon size={22} className="text-magma-red group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-heading text-lg text-dirt-brown uppercase mb-2">{cat.name}</h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      Buy, sell, or pawn {cat.text}. {city.name} residents get the same great prices and fair appraisals every time.
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 md:py-24 bg-dirt-brown noise-overlay torch-glow" data-testid="city-why-us">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute -top-1 left-0 w-full" style={{position:'relative',top:'-1px',marginBottom:'-1px'}}>
            <path d="M0 0L80 10C160 20 320 40 480 46C640 52 800 44 960 38C1120 32 1280 28 1360 26L1440 24V0H0Z" fill="#F4EBD9"/>
          </svg>
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-sm font-body font-semibold text-accent uppercase tracking-widest mb-3">Why {city.name} Chooses Us</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading text-bone-white uppercase chisel-heading">
                The Pawn Shop {city.name} <span className="text-accent">Trusts</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-bone-white/8 border-3 border-accent/20 rounded-rock-lg p-6 backdrop-blur-sm">
                <h3 className="font-heading text-xl text-accent uppercase mb-3">Pawn Loans - No Credit Check</h3>
                <p className="text-base text-stone-light font-body leading-relaxed">
                  Need quick cash in {city.name}? A pawn loan lets you borrow against your items with no credit check. Pay back the loan and get your items back. It is the fastest way to get cash when you need it — and Cave City Pawn is just {city.distance === 0 ? 'right around the corner' : `a quick ${city.distance}-mile drive away`}.
                </p>
              </div>
              <div className="bg-bone-white/8 border-3 border-magma-red/20 rounded-rock-lg p-6 backdrop-blur-sm">
                <h3 className="font-heading text-xl text-magma-red uppercase mb-3">Sell for Instant Cash</h3>
                <p className="text-base text-stone-light font-body leading-relaxed">
                  Ready to sell? We buy firearms, electronics, instruments, jewelry, tools, and archery equipment from {city.name} residents at fair market prices. Walk in with your items, walk out with cash. No hassle, no waiting, no strings attached. Over 35 years of appraisal experience.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-bone-white/8 border-3 border-bone-white/15 rounded-rock-lg p-6 backdrop-blur-sm">
              <h3 className="font-heading text-xl text-bone-white uppercase mb-3">Serving {fullName} &amp; Surrounding Areas</h3>
              <p className="text-base text-stone-light font-body leading-relaxed">
                Cave City Pawn Shop &amp; Trading Company is conveniently located at <strong className="text-accent">4417 Jackson Street, Marianna, Florida 32448</strong>.
                {city.distance > 0 && ` From ${city.name}, we are just ${city.distance} miles ${city.direction} — an easy drive for the best pawn shop experience in the area.`}
                {' '}We are open Monday through Friday 9 AM to 6 PM and Saturday 9 AM to 4 PM. Come visit us or call <strong className="text-accent">850-615-6105</strong> today!
              </p>
            </div>
          </div>
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" style={{position:'relative',bottom:'-1px',marginTop:'-1px'}}>
            <path d="M0 60L80 50C160 40 320 20 480 14C640 8 800 16 960 22C1120 28 1280 32 1360 34L1440 36V60H0Z" fill="#F4EBD9"/>
          </svg>
        </section>

        {/* CTA + Nearby Cities */}
        <section className="py-16 md:py-24 bg-background noise-overlay" data-testid="city-cta-nearby">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* CTA */}
            <div className="text-center mb-16 bg-card border-4 border-dirt-brown/15 rounded-rock-lg p-8 md:p-12 shadow-soft">
              <h2 className="text-2xl sm:text-3xl font-heading text-dirt-brown uppercase mb-4">
                Visit Cave City Pawn Today
              </h2>
              <p className="text-base text-muted-foreground font-body max-w-xl mx-auto mb-6">
                {city.distance === 0
                  ? 'Stop by our shop on Jackson Street for the best deals in town!'
                  : `Make the short ${city.distance}-mile trip from ${city.name} to Marianna for deals you will not find anywhere else.`
                }
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="tel:850-615-6105" className="cave-btn inline-flex items-center gap-2 px-7 py-3.5 bg-magma-red text-white font-heading text-base uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                  <Phone size={20} /> Call Now
                </a>
                <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer"
                  className="cave-btn inline-flex items-center gap-2 px-7 py-3.5 bg-accent text-dirt-brown font-heading text-base uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                  <ShoppingCart size={20} /> Shop Online
                </a>
              </div>
            </div>

            {/* Nearby Cities */}
            {nearbyCities.length > 0 && (
              <div>
                <h2 className="text-xl sm:text-2xl font-heading text-dirt-brown uppercase text-center mb-8 chisel-heading">
                  Also Serving <span className="text-magma-red">Nearby Areas</span>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {nearbyCities.map(nc => (
                    <Link key={nc.slug} to={getCityUrl(nc)} data-testid={`nearby-${nc.slug}`}
                      className="stone-card bg-card border-2 border-dirt-brown/10 rounded-rock-lg p-4 text-center hover:border-magma-red/30 transition-all group">
                      <p className="font-heading text-sm text-dirt-brown uppercase group-hover:text-magma-red transition-colors">{nc.name}</p>
                      <p className="text-xs text-muted-foreground font-body">{nc.state} &middot; {nc.distance} mi</p>
                    </Link>
                  ))}
                </div>
                <div className="text-center mt-6">
                  <Link to="/locations" className="text-sm font-body font-semibold text-magma-red hover:text-dirt-brown transition-colors underline underline-offset-4">
                    View All Service Areas
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative bg-foreground text-bone-white py-10">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-dirt-brown via-stone-dark to-dirt-brown" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Cave City Pawn Shop" className="h-12" />
              <div>
                <p className="font-body font-bold text-bone-white">4417 Jackson Street, Marianna, FL 32448</p>
                <a href="tel:850-615-6105" className="text-accent font-body font-bold hover:text-white transition-colors">850-615-6105</a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-stone-light font-body hover:text-accent transition-colors">Home</Link>
              <Link to="/locations" className="text-sm text-stone-light font-body hover:text-accent transition-colors">All Locations</Link>
              <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer" className="text-sm text-stone-light font-body hover:text-accent transition-colors">Shop Online</a>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-stone-dark/30 text-center">
            <p className="text-xs text-stone-dark font-body">&copy; {new Date().getFullYear()} Cave City Pawn Shop &amp; Trading Company. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
